import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import {
  TourButtonConfig,
  TourStepConfig,
  TourStepPosition,
} from "../../models/tour-step.model";
import { resolveTourStepWidth } from "../../tour-styles";

/** Maps step placement to tooltip arrow CSS class before layout is resolved. */
function mapStepPositionToArrowClass(
  stepPosition?: TourStepPosition,
): TourStepPosition {
  switch (stepPosition) {
    case "top":
      return "top";
    case "bottom":
      return "bottom";
    case "left":
      return "left";
    case "right":
      return "right";
    default:
      return "bottom";
  }
}

@Component({
  selector: "app-tour-tooltip",
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="tour-tooltip"
      [ngClass]="positionClass"
      [ngStyle]="mergedTooltipStyles"
    >
      <div class="tour-tooltip-header">
        <div>
          <div class="tour-tooltip-title">{{ step?.title }}</div>
          <div class="tour-tooltip-text">{{ step?.text }}</div>
        </div>
        <div class="tour-tooltip-header-right">
          <button
            *ngIf="step?.allowSkip"
            type="button"
            class="tour-tooltip-skipbtn"
            (click)="onSkip()"
          >
            {{ step?.skipText ?? "Skip" }}
          </button>
        </div>
      </div>

      <div *ngIf="stepCount > 0" class="tour-tooltip-progress">
        {{ currentStepIndex + 1 }} / {{ stepCount }}
      </div>

      <div class="tour-tooltip-actions">
        <button
          *ngFor="let button of step?.buttons ?? []"
          [ngClass]="button.classes || 'btn btn-secondary btn-sm'"
          [ngStyle]="button.styles"
          type="button"
          (click)="onAction(button)"
        >
          {{ button.text }}
        </button>
      </div>
    </div>
  `,
  styleUrls: ["./tour-tooltip.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourTooltipComponent {
  @Input() step?: TourStepConfig;
  @Input() currentStepIndex = 0;
  @Input() stepCount = 0;
  @Input() tooltipStyles: Record<string, string | number> = {};
  @Output() buttonClick = new EventEmitter<TourButtonConfig>();

  private _actualPosition?: TourStepPosition;

  @Input()
  set actualPosition(value: TourStepPosition | undefined) {
    this._actualPosition = value;
    this.cd.markForCheck();
  }

  get actualPosition(): TourStepPosition | undefined {
    return this._actualPosition;
  }

  get positionClass(): string {
    return (
      this.actualPosition ??
      mapStepPositionToArrowClass(this.step?.position)
    );
  }

  get mergedTooltipStyles(): Record<string, string | number> {
    const styles = { ...this.tooltipStyles };

    if (this.step?.width !== undefined) {
      const { width, maxWidth } = resolveTourStepWidth(this.step.width);
      styles["--tour-tooltip-width"] = width;
      styles["--tour-tooltip-max-width"] = maxWidth;
    }

    return styles;
  }

  constructor(private readonly cd: ChangeDetectorRef) {}

  onAction(button: TourButtonConfig): void {
    this.buttonClick.emit(button);
  }

  onSkip(): void {
    this.onAction({
      text: this.step?.skipText ?? "Skip",
      action: "cancel",
      classes: "tour-tooltip-skip",
    });
  }
}
